import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { TextField } from "src/ui/shared/form/TextField";
import { EventCategoryHeader } from "src/ui/shared/table/headers/EventCategoryHeader";
import { EventLevelHeader } from "src/ui/shared/table/headers/EventLevelHeader";

import { EventReport } from "@dashboard/devices/types";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Card, CardContent, CardHeader, TableRow, Typography, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { Tooltip } from "@shared/Tooltip";
import { ColumnFiltersState, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import { useGetAllEvents } from "../../hooks/useGetAllEvents";

import { useStyles } from "./AdminEventsPage.style";
import {
    columns,
    FormValues,
    getEventCategory,
    getEventLevel,
    isFilterApplied,
    parseIdList,
    parseNumericIdList,
} from "./AdminEventsPage.utils";
import EventRow from "./EventRow";

const AdminEventsPage = () => {
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(12);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [filterState, setFilterState] = React.useState<FormValues>({});
    const [searchTerm, setSearchTerm] = React.useState("");
    const [sorting, setSorting] = React.useState<SortingState>([]);

    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();

    const filterActive = isFilterApplied(filterState);

    const levels = React.useMemo(() => getEventLevel(columnFilters), [columnFilters]);
    const categories = React.useMemo(() => getEventCategory(columnFilters), [columnFilters]);
    const workspaceIds = React.useMemo(() => parseNumericIdList(filterState.workspaceId), [filterState.workspaceId]);
    const groupIds = React.useMemo(() => parseNumericIdList(filterState.groupId), [filterState.groupId]);
    const entityIds = React.useMemo(() => parseIdList(filterState.entityId), [filterState.entityId]);

    const { data, isLoading } = useGetAllEvents({
        pageIndex: pageIndex + 1,
        pageSize,
        levels,
        categories,
        workspaceIds,
        groupIds,
        entityIds,
        searchTerm: searchTerm || undefined,
        sortCriteria: sorting[0]?.id,
        sortDirection: sorting[0] ? (sorting[0].desc ? "descending" : "ascending") : undefined,
    });

    const table = useReactTable<EventReport>({
        data: data.list,
        columns,
        state: { sorting },
        manualSorting: true,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const onChangeColumnFilters = (updater: React.SetStateAction<ColumnFiltersState>) => {
        setColumnFilters(updater);
        setPageIndex(0);
    };

    const onSearchTermChange = (value: string) => {
        setSearchTerm(value);
        setPageIndex(0);
    };

    const onFilterClear = (form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset({});
        setFilterState({});
        setPageIndex(0);
    };

    const onSearchTermClear = () => {
        setSearchTerm("");
        setPageIndex(0);
    };

    const onTableClear = (form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset({});
        setColumnFilters([]);
        setFilterState({});
        setSearchTerm("");
        setPageIndex(0);
    };

    const onSubmit = (val: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
        form.reset(val);
        setFilterState(val);
        setPageIndex(0);
    };

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, data.list.length]);

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            render={({ handleSubmit, form, pristine }) => (
                <form onSubmit={handleSubmit}>
                    <PageContainer fitViewport>
                        <Card className={classes.card}>
                            <CardHeader
                                title={
                                    <Box className={classes.cardTitleBox}>
                                        <Typography variant="h6">{t("pages:adminEventsPage.filters")}</Typography>

                                        <Tooltip title={t("common:tooltip.commaSeparatedValues")} placement="right">
                                            <i className="fas fa-info-circle" style={{ fontSize: 16 }} />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                            <CardContent className={classes.cardContent}>
                                <TextField name="workspaceId" label={t("common:label.workspaceId")} />
                                <TextField name="groupId" label={t("common:label.groupId")} />
                                <TextField name="entityId" label={t("common:label.entityId")} />

                                <Box className={classes.filterActions}>
                                    {filterActive ? (
                                        <Tooltip title={t("pages:adminEventsPage.youHaveActiveFilters")} placement="left">
                                            <i className="fas fa-info-circle" style={{ color: theme.palette.warning.main }} />
                                        </Tooltip>
                                    ) : null}

                                    <PrimaryButton type="submit" disabled={pristine}>
                                        {t("common:button.apply")}
                                    </PrimaryButton>
                                    <SecondaryButton disabled={_.isEmpty(filterState)} onClick={() => onFilterClear(form)}>
                                        {t("common:button.clear")}
                                    </SecondaryButton>
                                </Box>
                            </CardContent>
                        </Card>
                        <Box className={classes.toolbar}>
                            <Box className={classes.searchContainer}>
                                <QueryBuilderField
                                    value={searchTerm}
                                    placeholder={t("common:button.search")}
                                    onChange={onSearchTermChange}
                                    onClear={() => onSearchTermClear()}
                                />
                            </Box>
                            <Tooltip title={t("common:tooltip.searchDataFieldOnly")}>
                                <i className="fas fa-exclamation-circle" />
                            </Tooltip>
                        </Box>
                        <Box className={layoutClasses.tableLayout}>
                            <Box className={layoutClasses.tableWrapper}>
                                <MinimalTableContainer
                                    ref={tableContainerRef}
                                    className={cx(
                                        layoutClasses.tableContainer,
                                        !contentOverflows && layoutClasses.tableContainerNoScroll,
                                    )}
                                >
                                    <MinimalTable>
                                        <MinimalTableHeader>
                                            {table.getHeaderGroups().map((headerGroup) => (
                                                <TableRow key={headerGroup.id}>
                                                    {headerGroup.headers.map((header) => (
                                                        <Header key={header.id} {...header}>
                                                            {header.id === "level" ? (
                                                                <EventLevelHeader
                                                                    columnFilters={columnFilters}
                                                                    setColumnFilters={onChangeColumnFilters}
                                                                />
                                                            ) : header.id === "event" ? (
                                                                <EventCategoryHeader
                                                                    columnFilters={columnFilters}
                                                                    setColumnFilters={onChangeColumnFilters}
                                                                />
                                                            ) : null}
                                                        </Header>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </MinimalTableHeader>
                                        <TableBody
                                            table={table}
                                            filter={{
                                                isActive: !!columnFilters.length || filterActive || !!searchTerm.length,
                                                onResetFilter: () => onTableClear(form),
                                            }}
                                            loading={isLoading}
                                            noDataMessage={t("pages:adminEventsPage.noEvents")}
                                        >
                                            {table.getRowModel().rows.map((row) => (
                                                <EventRow key={row.original.id} row={row} />
                                            ))}
                                        </TableBody>
                                    </MinimalTable>
                                </MinimalTableContainer>
                            </Box>
                            <CustomTablePagination
                                component="div"
                                className={layoutClasses.pagination}
                                rowsPerPageOptions={[12, 24, 48]}
                                count={data.count}
                                rowsPerPage={pageSize}
                                page={pageIndex}
                                onPageChange={(_event, page) => setPageIndex(page)}
                                onRowsPerPageChange={(e) => {
                                    const size = e.target.value ? Number(e.target.value) : 12;
                                    setPageSize(size);
                                    setPageIndex(0);
                                }}
                            />
                        </Box>
                    </PageContainer>
                </form>
            )}
        />
    );
};

export default withRequiredRole(
    withMetaDescription(AdminEventsPage, {
        title: i18next.t("pages:adminEventsPage.metaTitle"),
        description: i18next.t("pages:adminEventsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
